import { IconAngleLeft, IconAngleRight, IconClock, mapper } from "@maia/icons";
import {
  BigPricingPill,
  ButtonLink,
  Divider,
  FeaturedIcon,
  ProductIntro,
  Stepper,
  TextButton,
  Typography,
} from "@maia/ui";
import { formatCurrency } from "@maia/utils";
import { useQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  linkOptions,
  Outlet,
  useMatches,
  useRouter,
} from "@tanstack/react-router";
import { useMemo } from "react";
import { twMerge } from "tailwind-merge";
import * as v from "valibot";
import { getCartQueryOptions } from "../../../../../../../../features/add-insurance/get-cart/queryOptions";
import { getProductsOptions } from "../../../../../../../../features/add-insurance/get-products/queryOptions";
import {
  getNextProduct,
  isFinalProduct,
  parseProductsFromQuery,
} from "../../../../../../../../features/add-insurance/utils";
import { getCustomerOptions } from "./../../../../../../../../features/customer/customer/queryOptions";

export const Route = createFileRoute(
  "/_authenticated/add-insurance/$customer/_layout-cart/cart/$cart/product/$product/_layout-cart",
)({
  component: RouteComponent,
  loader: async ({
    context: { queryClient },
    params: { customer: customerGuid, cart, product },
  }) => {
    const { specialLimit } = await queryClient.ensureQueryData(
      getCartQueryOptions({ cartGuid: cart }),
    );

    const products = await queryClient.ensureQueryData(
      getProductsOptions({
        cartGuid: cart,
        customerGuid: customerGuid,
      }),
    );

    const customer = await queryClient.ensureQueryData(getCustomerOptions(customerGuid));

    return { specialLimit, products, customer };
  },
  validateSearch: v.object({
    products: v.optional(v.string()),
  }),
});

function RouteComponent() {
  const { products: initialProducts, customer } = Route.useLoaderData();
  const { cart, product } = Route.useParams();
  const { products: allProducts } = Route.useSearch();
  const matches = useMatches();
  const router = useRouter();

  const { data: products } = useQuery({
    ...getProductsOptions({
      cartGuid: cart,
      customerGuid: customer.guid,
    }),
    initialData: initialProducts,
  });

  const activeProduct = products.find((p) => p.guid === product);

  const isCompositionPage =
    matches.find(
      (match) =>
        match.fullPath === "/add-insurance/$customer/cart/$cart/product/$product/compositions",
    )?.status === "success";

  const parsedSearchPruducts = parseProductsFromQuery(allProducts);
  const isFinal = isFinalProduct(product, parsedSearchPruducts);
  const submitButtonLink = isFinal
    ? linkOptions({
        to: "/add-insurance/$customer/cart/$cart/summary",
        params: { customer: customer.guid, cart },
        search: { products: allProducts ?? "" },
      })
    : linkOptions({
        to: "/add-insurance/$customer/cart/$cart/product/$product",
        params: {
          customer: customer.guid,
          cart,
          product: getNextProduct(product, parsedSearchPruducts),
        },
        search: { products: allProducts },
      });

  const totalPrice = useMemo(
    () =>
      products.reduce(
        (acc, product) => acc + (Number(product.totalPrice) ? Number(product.totalPrice) : 0),
        0,
      ),
    [products],
  );
  const totalTax = useMemo(
    () =>
      products.reduce(
        (acc, product) =>
          acc + ((Number(product.totalTax) ?? 0) ? (Number(product.totalTax) ?? 0) : 0),
        0,
      ),
    [products],
  );

  //   const headerContent = useMemo(() => {
  //     const content = cmsContent?.[0]?.elements?.find(
  //       (element) => element.identifier === 'lists_insured-box',
  //     );
  //     return {
  //       title: content?.fields.find((field) => field.key === 'title')?.value,
  //       items: content?.elements.map((element) => element.markdown),
  //     };
  //   }, [cmsContent]);

  return (
    <div className="layout-sidebar">
      <div className="area-page-content content-grid">
        <div className="-ml-32 -mr-32 col-span-full">
          <div className="area-page-header px-32 flex items-center justify-between">
            <TextButton
              as="button"
              onPress={() => router.history.back()}
              intent="standard"
              size="tiny"
              iconLeft={IconAngleLeft}
            >
              Terug
            </TextButton>
            <ul className="flex w-[286px] ml-auto justify-end">
              {products.map((product) => {
                const isActive = product.guid === activeProduct?.guid;
                const isLastItem = products.indexOf(product) === products.length - 1;
                return (
                  <li
                    key={product.guid}
                    className={twMerge("flex items-center", !isLastItem && "grow")}
                  >
                    <FeaturedIcon
                      icon={mapper(product.icon)}
                      variant={isActive ? "dark-square" : "light-square"}
                    />
                    {!isLastItem && <Divider className="w-full" />}
                  </li>
                );
              })}
            </ul>
          </div>
          <ProductIntro
            title={activeProduct?.name}
            icon={mapper(activeProduct?.icon)}
            usps={undefined}
            // onClickMoreInformation={alert}
            className="px-32 pt-24 pb-32"
          />
          {activeProduct?.isComposition && (
            <Stepper
              className="py-24 border-y border-grayscale100"
              links={[
                linkOptions({
                  to: "/add-insurance/$customer/cart/$cart/product/$product",
                  params: {
                    product,
                    customer: customer.guid,
                    cart,
                  },
                  label: "Premiebepalende factoren",
                }),
                linkOptions({
                  to: "/add-insurance/$customer/cart/$cart/product/$product/compositions",
                  params: {
                    product,
                    customer: customer.guid,
                    cart,
                  },
                  label: "Configureren",
                }),
              ]}
            />
          )}
        </div>
        <div className="col-span-full">
          <Outlet />
        </div>
      </div>
      <div className="flex flex-col area-page-sidebar">
        <Typography variant="display6" weight="medium" className="text-primaryMain mb-32">
          Samenstellen
        </Typography>
        <div className="mb-12">
          <div className="flex items-center gap-x-12">
            {activeProduct?.totalPrice ? (
              <div className="flex flex-col">
                <BigPricingPill
                  price={activeProduct?.totalPrice}
                  variant="ghost"
                  size="lg"
                  billingUnit={customer.billingUnit}
                />
                <Typography variant="body-xs" weight="regular" className="text-grayscale400">
                  Incl. assurantiebelasting van {formatCurrency(activeProduct.totalTax)}
                </Typography>
              </div>
            ) : (
              <div className="flex flex-col gap-y-8">
                <div className="flex gap-x-12">
                  <IconClock className="size-32 fill-info300" />
                  <Typography variant="display5" weight="semibold" className="text-info300">
                    Wachten op gegevens
                  </Typography>
                </div>
                <Typography variant="body-xs" weight="regular" className="text-grayscale300">
                  Vul alle gegevens in om de premie te berekenen.
                </Typography>
              </div>
            )}
          </div>
        </div>
        <Divider className="-ml-32 -mr-32 mb-12" />
        <ul className="flex flex-col gap-y-24 mb-12 grow">
          {products.map((product) => {
            const isActive = product.guid === activeProduct?.guid;
            return (
              <li
                key={product.guid}
                className={twMerge(isActive && "grow", isActive ? "order-1" : "order-2")}
              >
                <div className="flex items-center gap-x-8 mb-8">
                  <FeaturedIcon icon={mapper(product.icon)} variant="light-square" size="sm" />
                  <Typography variant="body-xs" weight="semibold">
                    {product.name}
                  </Typography>
                </div>
                <div>
                  <ul className="flex flex-col gap-y-8">
                    {isActive &&
                      product.addOns
                        ?.filter((addon) => addon.answer.value === "J")
                        .map((addon) => (
                          <li key={addon.guid} className="flex flex-col gap-y-4">
                            <Typography
                              key={addon.guid}
                              variant="body-xs"
                              weight="medium"
                              className="text-grayscale500"
                            >
                              {addon.phrasing}
                            </Typography>
                            {addon.children?.map((child) => (
                              <Typography
                                key={addon.guid}
                                variant="body-xs"
                                weight="regular"
                                className="text-grayscale400"
                              >
                                {
                                  child.options.find(
                                    (option) => option.value === child.answer.value,
                                  )?.label
                                }
                              </Typography>
                            ))}
                          </li>
                        ))}
                  </ul>
                </div>
              </li>
            );
          })}
        </ul>
        <Divider className="-ml-32 -mr-32 mb-12" />
        <div>
          <div className="flex justify-between">
            <Typography variant="body-s" weight="medium">
              Totaal {customer.billingUnit.label}
            </Typography>
            <Typography variant="body-s" weight="medium">
              {formatCurrency(totalPrice)}
            </Typography>
          </div>
          <div className="flex justify-between mb-24">
            <Typography variant="body-xs" weight="regular" className="text-grayscale400">
              Assurantiebelasting
            </Typography>
            <Typography variant="body-xs" weight="regular" className="text-grayscale400">
              {formatCurrency(totalTax)}
            </Typography>
          </div>
          {isCompositionPage && (
            <ButtonLink
              {...submitButtonLink}
              intent="primary"
              iconRight={IconAngleRight}
              className="mt-auto w-full justify-center"
              disabled={!isCompositionPage}
            >
              Volgende
            </ButtonLink>
          )}
        </div>
      </div>
    </div>
  );
}
